import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { useConfigurationVersions } from "@/hooks/useConfigurationVersions";
import { useVersionMetrics } from "@/hooks/useVersionMetrics";
import { formatDistanceToNow } from "date-fns";
import { History, Tag, GitCompare, RotateCcw, ChevronDown, ChevronUp, BarChart3, List } from "lucide-react";
import { AppConfigurationVersion } from "@/types/application";
import { RollbackConfirmationDialog } from "./RollbackConfirmationDialog";
import { VersionComparisonView } from "./VersionComparisonView";
import { VersionTimelineChart } from "./VersionTimelineChart";
import { VersionMetricsSummary } from "./VersionMetricsSummary";
import { VersionTimelineFilters } from "./VersionTimelineFilters";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface ConfigVersionHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  configurationId: string;
  configurationName: string;
  currentVersion?: number;
}

export const ConfigVersionHistoryDialog = ({
  open,
  onOpenChange,
  configurationId,
  configurationName,
  currentVersion
}: ConfigVersionHistoryDialogProps) => {
  const { versions, isLoading, rollbackToVersion, labelVersion } = useConfigurationVersions(configurationId);
  const { metrics, filters, setFilters, summary } = useVersionMetrics(versions);
  const [expandedVersions, setExpandedVersions] = useState<Set<string>>(new Set());
  const [rollbackVersionId, setRollbackVersionId] = useState<string | null>(null);
  const [compareVersionIds, setCompareVersionIds] = useState<[string | null, string | null]>([null, null]);
  const [labelingVersionId, setLabelingVersionId] = useState<string | null>(null);
  const [labelText, setLabelText] = useState("");
  const [isMilestone, setIsMilestone] = useState(false);
  const [activeTab, setActiveTab] = useState<'list' | 'analytics'>('list');

  const toggleExpanded = (versionId: string) => {
    const newExpanded = new Set(expandedVersions);
    if (newExpanded.has(versionId)) {
      newExpanded.delete(versionId);
    } else {
      newExpanded.add(versionId);
    }
    setExpandedVersions(newExpanded);
  };

  const handleRollback = async (versionId: string) => {
    await rollbackToVersion(versionId);
    setRollbackVersionId(null);
    onOpenChange(false);
  };

  const handleLabelSave = async (versionId: string) => {
    if (labelText.trim()) {
      await labelVersion(versionId, labelText.trim(), isMilestone);
      setLabelingVersionId(null);
      setLabelText("");
      setIsMilestone(false);
    }
  };

  const renderChangeDetails = (version: AppConfigurationVersion) => {
    if (!version.changeDetails) return null;

    return (
      <div className="mt-2 space-y-1 text-sm text-muted-foreground">
        {version.changeDetails.changes.map((change, idx) => (
          <div key={idx} className="flex items-start gap-2">
            <span className="text-xs">•</span>
            <span>
              {change.field === 'keys' && change.action === 'added' && (
                <span className="text-success dark:text-success">
                  Added key: <code className="font-mono">{change.key}</code>
                </span>
              )}
              {change.field === 'keys' && change.action === 'modified' && (
                <span className="text-warning dark:text-warning">
                  Modified key: <code className="font-mono">{change.key}</code>
                </span>
              )}
              {change.field === 'keys' && change.action === 'removed' && (
                <span className="text-destructive dark:text-destructive">
                  Removed key: <code className="font-mono">{change.key}</code>
                </span>
              )}
              {change.field === 'name' && <span>Changed name</span>}
              {change.field === 'description' && <span>Updated description</span>}
              {change.field === 'customers' && <span>Modified customer assignments</span>}
              {change.field === 'isActive' && (
                <span>{change.newValue ? 'Activated' : 'Deactivated'}</span>
              )}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Version History: {configurationName}
            </DialogTitle>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'list' | 'analytics')}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="list">
                <List className="h-4 w-4 mr-2" />
                Timeline List
              </TabsTrigger>
              <TabsTrigger value="analytics">
                <BarChart3 className="h-4 w-4 mr-2" />
                Analytics
              </TabsTrigger>
            </TabsList>

            <TabsContent value="list" className="mt-4">
              <ScrollArea className="h-[600px] pr-4">
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">Loading versions...</div>
            ) : versions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No version history available yet. Changes will be tracked automatically.
              </div>
            ) : (
              <div className="space-y-4">
                {versions.map((version, index) => (
                  <div
                    key={version.id}
                    className="border rounded-lg p-4 space-y-3 hover:bg-accent/50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold">v{version.versionNumber}</span>
                          {index === 0 && (
                            <Badge variant="default">Current</Badge>
                          )}
                          {version.isMilestone && (
                            <Badge variant="secondary">
                              <Tag className="h-3 w-3 mr-1" />
                              Milestone
                            </Badge>
                          )}
                          {version.versionLabel && (
                            <Badge variant="outline">{version.versionLabel}</Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {formatDistanceToNow(new Date(version.createdAt), { addSuffix: true })}
                        </div>
                        {version.changeSummary && (
                          <div className="text-sm mt-2">{version.changeSummary}</div>
                        )}
                      </div>

                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => toggleExpanded(version.id)}
                        >
                          {expandedVersions.has(version.id) ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </Button>
                        {index !== 0 && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setRollbackVersionId(version.id)}
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setLabelingVersionId(version.id);
                            setLabelText(version.versionLabel || "");
                            setIsMilestone(version.isMilestone);
                          }}
                        >
                          <Tag className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {expandedVersions.has(version.id) && (
                      <div className="border-t pt-3">
                        {renderChangeDetails(version)}
                        <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                          <div>
                            <span className="font-medium">Name:</span> {version.name}
                          </div>
                          <div>
                            <span className="font-medium">Status:</span>{' '}
                            {version.isActive ? 'Active' : 'Inactive'}
                          </div>
                          <div>
                            <span className="font-medium">Keys:</span>{' '}
                            {version.configurationData.keys.length}
                          </div>
                          <div>
                            <span className="font-medium">Customers:</span>{' '}
                            {version.customers.length}
                          </div>
                        </div>
                      </div>
                    )}

                    {labelingVersionId === version.id && (
                      <div className="border-t pt-3 space-y-3">
                        <div>
                          <Label htmlFor={`label-${version.id}`}>Version Label</Label>
                          <Input
                            id={`label-${version.id}`}
                            value={labelText}
                            onChange={(e) => setLabelText(e.target.value)}
                            placeholder="e.g., Production Release"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id={`milestone-${version.id}`}
                            checked={isMilestone}
                            onCheckedChange={(checked) => setIsMilestone(checked as boolean)}
                          />
                          <Label htmlFor={`milestone-${version.id}`}>
                            Mark as milestone
                          </Label>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleLabelSave(version.id)}
                          >
                            Save Label
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setLabelingVersionId(null);
                              setLabelText("");
                              setIsMilestone(false);
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
              </ScrollArea>
            </TabsContent>

            <TabsContent value="analytics" className="mt-4">
              <ScrollArea className="h-[600px] pr-4">
                {isLoading ? (
                  <div className="text-center py-8 text-muted-foreground">Loading analytics...</div>
                ) : versions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No version history available yet
                  </div>
                ) : (
                  <div className="space-y-6">
                    <VersionMetricsSummary summary={summary} />
                    
                    <div className="grid grid-cols-12 gap-4">
                      <div className="col-span-3">
                        <VersionTimelineFilters 
                          filters={filters}
                          onFiltersChange={setFilters}
                        />
                      </div>

                      <div className="col-span-9">
                        {metrics.length > 0 ? (
                          <VersionTimelineChart metrics={metrics} />
                        ) : (
                          <div className="text-center py-12 text-muted-foreground">
                            No data matches the selected filters
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      <RollbackConfirmationDialog
        open={rollbackVersionId !== null}
        onOpenChange={(open) => !open && setRollbackVersionId(null)}
        onConfirm={() => rollbackVersionId && handleRollback(rollbackVersionId)}
        version={versions.find(v => v.id === rollbackVersionId)}
      />
    </>
  );
};
